/**
 * `/workspace` — the founder's overview, and the honest empty state for
 * everyone else.
 *
 * A member with no workspace is not an error to hide: their invite may have
 * been revoked, or they may have signed in before being added. They get the
 * plain sentence and the person to ask, not a spinner.
 *
 * Creating a workspace is founder-only server-side (403 otherwise); the form is
 * only rendered for founders, but the server is what enforces it.
 */
import { useState } from 'react'
import { Building2, Plus } from 'lucide-react'

import { api } from '@/api/client'
import { ErrorNote } from '@/components/ErrorNote'
import { Icon } from '@/components/Icon'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useSession } from '@/features/auth/SessionContext'
import { DataModeBadge } from '@/features/workspace/DataModeCard'
import {
  MAX_RETENTION_DAYS,
  MIN_RETENTION_DAYS,
} from '@/features/workspace/RetentionWindowCard'
import { useI18n } from '@/i18n'
import { Link, buildPath, navigate } from '@/lib/router'

export function WorkspaceIndex() {
  const { t } = useI18n()
  const { workspaces, isFounder, refresh } = useSession()
  const [name, setName] = useState('')
  // Empty means "the server's default", not 30: the default lives in one place
  // (`DEFAULT_RETENTION_DAYS`, `app/models.py`) and the field stays blank rather
  // than putting a second copy of it on the screen.
  const [retentionDays, setRetentionDays] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const create = async (event: React.FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const chosen = retentionDays.trim()
      const workspace = await api.createWorkspace(
        name.trim(),
        chosen === '' ? undefined : Number(chosen),
      )
      await refresh()
      navigate(buildPath('workspace', { workspaceId: workspace.id }))
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex max-w-[900px] flex-col gap-8">
      <section className="flex flex-col gap-3">
        <h1 className="font-display text-[32px] leading-tight font-semibold">
          {t('workspaceIndex.title')}
        </h1>
        <p className="max-w-[70ch] text-[15px] text-muted">
          {isFounder ? t('workspaceIndex.leadFounder') : t('workspaceIndex.leadMember')}
        </p>
      </section>

      {workspaces.length === 0 ? (
        <p className="rounded-sm border border-border bg-surface px-4 py-3 text-[13px] text-ink-2">
          {isFounder ? t('workspaceIndex.emptyFounder') : t('workspaceIndex.emptyMember')}
        </p>
      ) : null}

      <ul className="flex flex-col gap-3" data-testid="workspace-list">
        {workspaces.map((workspace) => (
          <li key={workspace.id}>
            <Card>
              <CardHeader>
                <CardTitle>
                  <Link
                    to={buildPath('workspace', { workspaceId: workspace.id })}
                    className="flex items-center gap-2 underline-offset-2 hover:underline"
                  >
                    <Icon icon={Building2} className="text-muted" />
                    {workspace.name}
                  </Link>
                </CardTitle>
                <CardDescription>{workspace.slug}</CardDescription>
              </CardHeader>
              <CardContent className="flex flex-wrap items-center gap-3">
                <DataModeBadge workspace={workspace} />
                <span className="text-[12px] text-muted">
                  {t('workspace.retention', { days: workspace.retention_days })}
                </span>
              </CardContent>
            </Card>
          </li>
        ))}
      </ul>

      {isFounder ? (
        <Card>
          <CardHeader>
            <CardTitle>{t('workspaceIndex.createTitle')}</CardTitle>
            <CardDescription>{t('workspaceIndex.createLead')}</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <form className="flex flex-wrap items-end gap-3" onSubmit={(e) => void create(e)}>
              {/* `min-w-0` so the flex item may shrink below the input's
                  280px at 320 CSS px (WCAG 2.2 §1.4.10 Reflow). */}
              <div className="flex min-w-0 flex-col gap-1.5">
                <label htmlFor="workspace-name" className="text-[13px] font-medium text-ink-2">
                  {t('workspaceIndex.nameLabel')}
                </label>
                <input
                  id="workspace-name"
                  type="text"
                  required
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  className="h-10 w-[280px] max-w-full min-w-0 rounded-sm border border-border-strong bg-white px-3 text-[15px] text-ink"
                />
              </div>
              <div className="flex min-w-0 flex-col gap-1.5">
                <label htmlFor="workspace-retention" className="text-[13px] font-medium text-ink-2">
                  {t('workspace.window.label')}
                </label>
                <input
                  id="workspace-retention"
                  type="number"
                  min={MIN_RETENTION_DAYS}
                  max={MAX_RETENTION_DAYS}
                  step={1}
                  value={retentionDays}
                  onChange={(event) => setRetentionDays(event.target.value)}
                  className="h-10 w-[120px] max-w-full min-w-0 rounded-sm border border-border-strong bg-white px-3 text-[15px] text-ink"
                />
              </div>
              <Button type="submit" disabled={busy || name.trim() === ''}>
                <Icon icon={Plus} />
                {t('workspaceIndex.create')}
              </Button>
            </form>
            <p className="text-[12px] text-muted">
              {t('workspace.window.optional')}{' '}
              {t('workspace.window.lead', { min: MIN_RETENTION_DAYS, max: MAX_RETENTION_DAYS })}
            </p>
            {error ? <ErrorNote message={error} /> : null}
          </CardContent>
        </Card>
      ) : null}
    </div>
  )
}
